import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Check, Loader2, Sparkles } from "lucide-react";
import { unitDisplayLabel, collapseUnitChoices, unitsAreNamed } from "@shared/weight";

export type ResolvedWeightUnit = {
  id: string;
  weight: number;
  priceCents: number;
  label?: string | null;
};

type AvailableUnit = {
  id: string;
  weight: number;
  priceCents: number;
  // Named price options ("Per dozen") carry a label; weighed units don't.
  label?: string | null;
};

interface WeightSizePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
  tierLabel?: string | null;
  itemName?: string | null;
  availableUnits?: AvailableUnit[];
  onSelected: (unit: ResolvedWeightUnit) => void;
}

export function WeightSizePicker({
  open,
  onOpenChange,
  itemId,
  tierLabel,
  itemName,
  availableUnits,
  onSelected,
}: WeightSizePickerProps) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: fetchedUnits = [], isLoading: unitsLoading } = useQuery<
    AvailableUnit[]
  >({
    queryKey: ["/api/items", itemId, "weight-units"],
    enabled: open && !availableUnits,
    queryFn: async () => {
      const res = await fetch(`/api/items/${itemId}`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const detail = await res.json();
      return (detail?.availableUnits as AvailableUnit[]) ?? [];
    },
  });

  const units = availableUnits ?? fetchedUnits;
  // Named options repeat one row per unit of stock; show each option once.
  const named = unitsAreNamed(units);
  const choices = collapseUnitChoices(units);
  const heading = tierLabel
    ? `Choose your ${tierLabel.toLowerCase()}`
    : "Choose your size";

  const selectUnit = async (unitId: string | "auto") => {
    setPendingId(unitId);
    setError(null);
    try {
      const res = await fetch("/api/cart/select-weight-unit", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId, unitId }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      onSelected(data.unit as ResolvedWeightUnit);
      onOpenChange(false);
    } catch {
      setError("That one just went. Pick another size.");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader>
          <DrawerTitle>{heading}</DrawerTitle>
          <DrawerDescription>
            {/* Named options (half dozen / dozen) aren't weighed, so the
                weight copy would be nonsense for them. */}
            {named
              ? "Pick the size you want."
              : itemName
                ? `Each ${itemName} is weighed individually. Pick your exact weight or let us grab a middle one.`
                : "Each one is weighed individually. Pick your exact weight or let us grab a middle one."}
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-[calc(16px+env(safe-area-inset-bottom))]">
          {!named && (
          <button
            type="button"
            onClick={() => selectUnit("auto")}
            disabled={pendingId !== null || unitsLoading || units.length === 0}
            className="mb-3 flex w-full items-center gap-3 rounded-2xl border border-primary/40 bg-primary/10 px-4 py-3 text-left transition active:scale-[0.99] disabled:opacity-60"
            data-testid={`weight-pick-auto-${itemId}`}
          >
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
              {pendingId === "auto" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-foreground">
                Pick one for me
              </span>
              <span className="block text-xs text-muted-foreground">
                A middle-of-the-range one, priced at its exact weight
              </span>
            </span>
          </button>
          )}

          <div className="space-y-2">
            {choices.map(({ unit, remaining }) => (
              <button
                key={unit.id}
                type="button"
                onClick={() => selectUnit(unit.id)}
                disabled={pendingId !== null}
                className="flex w-full items-center justify-between gap-3 rounded-2xl border border-card-border bg-card px-4 py-3 text-left transition active:scale-[0.99] disabled:opacity-60"
                data-testid={`weight-pick-unit-${unit.id}`}
              >
                <span className="text-sm font-semibold text-foreground">
                  {unitDisplayLabel({ label: unit.label, weightCentiLb: unit.weight })}
                  {remaining > 1 && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      {remaining} left
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-sm font-bold text-primary">
                    ${(unit.priceCents / 100).toFixed(2)}
                  </span>
                  {pendingId === unit.id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    <Check className="h-4 w-4 text-transparent" />
                  )}
                </span>
              </button>
            ))}
          </div>

          {units.length === 0 &&
            (unitsLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-muted-foreground">
                These are all spoken for right now.
              </p>
            ))}

          {error && (
            <p className="mt-3 text-center text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
